import { customElement, property, state } from "lit/decorators.js";
import { connect } from "pwa-helpers";
import store from "../../../store/store";
import { BaseComponent } from "../../base-component/base-component";
import { getShopCart } from "../../cart/shop-cart/thunks/get-shop-cart.thunk";
import { html, nothing, render } from "lit";
import { mapWishListItemsToProducts } from "../utils/wish-list-product.util";
import { bulkAddToCart } from "../../cart/shop-cart/thunks/bulk-add-to-cart.thunk";
import { WishList } from "../interfaces/wish-list-items.interface";
import { isBulkAddToCartFailed, isBulkAddToCartLoading, isBulkAddToCartSuccess } from "../utils/wish-list.util";
import { smallSpinner } from "../../../common/constants/api-constants";
import { getGlobalToastContainer, renderAddAllItemsToCartSuccessToast, renderErrorToast } from "../utils/wish-list-toast.util";
import { ErrorToastWishList } from "../constants/wish-list-error-toast-status";
import { WishListAnalytics } from "../utils/wish-list-analytics.util";
import { ProductCardData } from "../../product-card/product-card-display";

const BUTTON_TEXT = 'Add All To Cart';

@customElement('mhecomm-wish-list-add-all-to-cart-button')
export class WishListAddAllToCartButton extends connect(store)(BaseComponent) {

  @property({ type: Object })
  wishList?: WishList;

  @property({ type: Boolean })
  productCatalogueOffCanvas = false;

  @property()
  pageType?: string;

  @state()
  products: ProductCardData[] = [];

  @state()
  bulkAddToCartLoading = false;

  @state()
  bulkAddToCartSuccess = false;

  @state()
  bulkAddToCartFailed = false;

  /**
   * The analytics instance for the wish list.
   */
  private readonly analytics: WishListAnalytics;

  constructor() {
    super();
    this.analytics = new WishListAnalytics();
  }

  connectedCallback() {
    super.connectedCallback();
    store.dispatch(getShopCart({}));
  }

  async stateChanged() {
    if (this.wishList) {
      this.products = mapWishListItemsToProducts(this.wishList.wishlist_items);
    }
    this.bulkAddToCartLoading = isBulkAddToCartLoading();
    this.bulkAddToCartSuccess = isBulkAddToCartSuccess();
    this.bulkAddToCartFailed = isBulkAddToCartFailed();
  }

  renderComp() {
    return html`
      <div class="position-relative">
        <div class="${this.productCatalogueOffCanvas ? '' : 'position-absolute bottom-0 end-0'}">
          <button
            type="button"
            id="add-all-items-to-cart-btn"
            class="btn btn-danger text-start"
            aria-label="${BUTTON_TEXT} Button"
            @click=${() => this._addAlltoCart()}
          >
          ${BUTTON_TEXT} ${this.bulkAddToCartFailed ? 
            this.displayToast(true) :
            this.bulkAddToCartLoading 
              ? html`${smallSpinner()}`
              : this.bulkAddToCartSuccess 
                ? this._addAlltoCartSuccess()
                : nothing
            }
          </button>
        </div>
      </div>
    `;
  }

  private _addAlltoCart() {
    const isbnArr = this.products!.map(product => product!.isbn13 ?? product!.isbn).filter((item): item is string => item !== undefined);
    this.analytics.addAllItemsToCart(this, this.products, this.wishList!, this.pageType!, BUTTON_TEXT);
    store.dispatch(bulkAddToCart({isbns: isbnArr, addItemsToMineCart: true}));
  }

  private _addAlltoCartSuccess() {
    this.displayToast(false);
  }

  /**
   * Displays a toast depending on the wish list add all to cart.
   */
  private displayToast(isError: boolean): void {
    const toastGlobalContainer: HTMLElement | undefined = getGlobalToastContainer();
    if (!toastGlobalContainer) {
      return;
    }

    if (isError) {
      render(renderErrorToast({
        error: ErrorToastWishList.BULK_ADD_TO_CART,
        displayLoadingTemplate: false,
        autoHide: false,
        hideClose: true,
        hideHeader: true,
        showOnlyBody: true,
      }), toastGlobalContainer);
      return;
    }

    render(renderAddAllItemsToCartSuccessToast(this.products?.length), toastGlobalContainer);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-wish-list-add-all-to-cart-button': WishListAddAllToCartButton;
  }
}